import { Text } from '@wix/design-system';
import {
  SparkleIcon,
  useUpgradeRecommendationCopy,
  useUpgradeRecommendedProduct,
} from '@wix/package-picker-shared-eclipse';
import React from 'react';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import { useQuickPayView } from '../QuickPayLightbox/QuickPayLightboxChrome';
import s from './AiPlanRecommendationCard.scss';
import { DATA_HOOKS } from './constants';

const useReportUpgradeRecommendation = (aiText: string, isSettled: boolean) => {
  const { productId, productName, cycleId, isFallback, requestId } =
    useUpgradeRecommendedProduct();
  const quickPayView = useQuickPayView();
  const report =
    EclipsePackagePickerModule.internals.bi?.onAiRecommendationComponent;
  const hasReportedRef = React.useRef(false);

  React.useEffect(() => {
    if (hasReportedRef.current || !report || !productId || !isSettled) {
      return;
    }
    if (quickPayView === 'card') {
      return;
    }
    hasReportedRef.current = true;
    (['load', 'view'] as const).forEach((action) =>
      report({
        action,
        aiText,
        recommendation: productName,
        productId,
        cycleId,
        source: isFallback ? 'catalog' : 'second_upgrade',
        correlationId: requestId,
      }),
    );
  }, [
    report,
    productId,
    productName,
    cycleId,
    aiText,
    quickPayView,
    isFallback,
    requestId,
    isSettled,
  ]);
};

export const UpgradeRecommendationCard = () => {
  const { t } = EclipsePackagePickerModule.internals.useTranslations();
  const { productId } = useUpgradeRecommendedProduct();
  const copy = useUpgradeRecommendationCopy();
  const title = copy?.title ?? '';
  const subtitle = copy?.subtitle;
  useReportUpgradeRecommendation(
    [title, subtitle].filter(Boolean).join(' | '),
    copy?.isSettled ?? false,
  );

  if (!productId) {
    return null;
  }

  return (
    <div
      className={s.card}
      data-hook={DATA_HOOKS.root}
      role="region"
      aria-label={t(
        'premium-purchase-plan.eclipse.ai-recommendation.aria-label',
      )}
    >
      <div className={s.headerRow}>
        <div className={s.headerLeft}>
          <SparkleIcon svgClassName={s.headerSparkle} />
          <Text
            size="small"
            weight="bold"
            className={s.insightText}
            dataHook={DATA_HOOKS.title}
          >
            {title}
          </Text>
        </div>
      </div>
      {subtitle ? (
        <Text
          size="small"
          className={s.upgradeSubtitle}
          dataHook={DATA_HOOKS.subtitle}
        >
          {subtitle}
        </Text>
      ) : null}
    </div>
  );
};
